import { DateRange, Mode } from '@daypicker/react'

import { AstraDayPicker, AstraDayPickerProps } from '../AstraDayPicker/AstraDayPicker'
import { getWeekSelectionRange, useWeekSelectionHelpers } from './hooks/useWeekSelectionHelpers'

type WeekPickerMode = Extract<Mode, 'single' | 'range'>

type Props = Omit<AstraDayPickerProps, 'fixedWeeks' | 'hideDuplicateWeeks' | 'ISOWeek' | 'mode' | 'onSelect' | 'selected' | 'showOutsideDays'> & {
    /**
     * The selection mode of the week picker
     */
    mode: WeekPickerMode
    /**
     * The currently selected date range
     */
    selected: DateRange | undefined
    /**
     * Callback fired when a date range is selected
     */
    onSelect: (range: DateRange | undefined) => void
}

/**
 * A date picker component that allows the user to select a single week or a range of weeks.
 */
export const AstraWeekPicker = ({ mode, modifiers, numberOfMonths, onDayClick, onDayMouseEnter, selected, onSelect, weekdaysOnly, ...rest }: Props) => {
    const selectedWeeks = getWeekSelectionRange(selected, weekdaysOnly)
    const { clearHoverRange, handleOnDayMouseEnter, handleSelect, hoverRange } = useWeekSelectionHelpers(
        mode,
        selectedWeeks,
        onSelect,
        rest.disabled,
        weekdaysOnly
    )

    return (
        <AstraDayPicker
            {...rest}
            fullWidth
            mode="range"
            selected={selectedWeeks}
            numberOfMonths={numberOfMonths ?? 2}
            showOutsideDays
            weekdaysOnly={weekdaysOnly}
            hideDuplicateWeeks
            onSelect={(_selectedRange, triggerDate) => handleSelect(triggerDate)}
            modifiers={{
                ...modifiers,
                hoverRange,
                hoverRangeStart: hoverRange?.from,
                hoverRangeEnd: hoverRange?.to,
            }}
            onDayClick={(date, dayModifiers, event) => {
                onDayClick?.(date, dayModifiers, event)
            }}
            onDayMouseEnter={(date, dayModifiers, event) => {
                handleOnDayMouseEnter(date)
                onDayMouseEnter?.(date, dayModifiers, event)
            }}
            onDayMouseLeave={clearHoverRange}
        />
    )
}
